import {SaveOutlined} from '@ant-design/icons';
import {
    Alert,
    AutoComplete,
    Button,
    Divider,
    Drawer,
    Flex,
    Form,
    Input,
    InputNumber,
    Select,
    Space,
    Spin,
    Typography,
} from 'antd';
import debounce from 'lodash/debounce';
import React, {useEffect, useMemo, useState} from 'react';

import {ApiQuery} from '../../../../../gen/api';
import {AdminEbayAspect, AdminEbayDraftReq, AdminEbayListing} from '../../../../../gen/api/models';
import {useNotification} from '../../../../../NotificationContext';
import {useScreenInfo} from '../../../../../ResponsiveGate';
import {formatPrice, parseMoney} from '../../../dashboard/helpers/slowSelling';
import {COMMON_CONDITIONS, conditionLabel} from './ebayLabels';

const {Text, Paragraph} = Typography;

const MAX_TITLE = 80;

type FormValues = Omit<AdminEbayDraftReq, 'price' | 'buyerShipping'> & {price?: number; buyerShipping?: number};

const errorMessage = (errors: Array<{message?: string}>) => errors?.[0]?.message;

/**
 * Filters the value list by what is typed. All values are kept (Brand has
 * thousands): the dropdown renders only the visible rows.
 */
function filterValues(values: string[], search: string): string[] {
    const needle = search.trim().toLowerCase();
    return needle ? values.filter((v) => v.toLowerCase().includes(needle)) : values;
}

/**
 * A free-text value with suggestions. Esc would clear what was typed;
 * here Esc only closes the suggestions (by leaving the field) and keeps the value.
 */
const TypedValueInput: React.FC<{
    id?: string;
    value?: string;
    onChange?: (value: string) => void;
    options: Array<{value: string; label: string}>;
    onSearch: (search: string) => void;
    maxLength?: number;
}> = ({id, value, onChange, options, onSearch, maxLength}) => (
    <div
        onKeyDownCapture={(e) => {
            if (e.key === 'Escape') {
                // The input is type="search": the browser itself clears it on Esc.
                e.preventDefault();
                e.stopPropagation();
                (e.target as HTMLElement).blur();
            }
        }}
    >
        <AutoComplete
            id={id}
            allowClear
            value={value}
            onChange={onChange}
            options={options}
            onSearch={onSearch}
            maxLength={maxLength}
            placeholder="Choose or type"
            style={{width: '100%'}}
        />
    </div>
);

/** One item specific. The form keeps every value as a list; single-value ones show one. */
const AspectField: React.FC<{aspect: AdminEbayAspect}> = ({aspect}) => {
    const [search, setSearch] = useState('');
    const options = useMemo(
        () => filterValues(aspect.values, search).map((v) => ({value: v, label: v})),
        [aspect.values, search]
    );
    const label = (
        <span>
            {aspect.name}
            {aspect.multiple && (
                <Text
                    type="secondary"
                    style={{fontSize: 12}}
                >
                    {' '}
                    (several)
                </Text>
            )}
        </span>
    );
    // Only the mark: an incomplete draft can be saved; the card lists what publishing still needs.
    const required = aspect.required;

    if (aspect.multiple) {
        return (
            <Form.Item
                name={['aspects', aspect.name]}
                label={label}
                required={required}
            >
                <Select
                    mode={aspect.selectionOnly ? 'multiple' : 'tags'}
                    options={options}
                    onSearch={setSearch}
                    filterOption={false}
                    placeholder={aspect.selectionOnly ? 'Choose' : 'Choose or type'}
                />
            </Form.Item>
        );
    }
    return (
        <Form.Item
            name={['aspects', aspect.name]}
            label={label}
            required={required}
            getValueProps={(value?: string[]) => ({value: value?.[0]})}
            normalize={(value?: string) => (value ? [value] : [])}
        >
            {aspect.selectionOnly ? (
                <Select
                    showSearch
                    allowClear
                    options={options}
                    onSearch={setSearch}
                    filterOption={false}
                    placeholder="Choose"
                />
            ) : (
                <TypedValueInput
                    options={options}
                    onSearch={setSearch}
                    maxLength={aspect.maxLength}
                />
            )}
        </Form.Item>
    );
};

export interface EbayDraftDrawerProps {
    productId: string;
    listing: AdminEbayListing;
    open: boolean;
    onClose: () => void;
    onSaved: (listing: AdminEbayListing) => void;
}

/** The eBay listing draft form: title, category, item specifics, condition, description, price. */
export const EbayDraftDrawer: React.FC<EbayDraftDrawerProps> = ({productId, listing, open, onClose, onSaved}) => {
    const notify = useNotification();
    const {screenMap} = useScreenInfo();
    const [form] = Form.useForm<FormValues>();

    const categoryId = Form.useWatch('categoryId', form);
    const condition = Form.useWatch('condition', form);
    const [categorySearch, setCategorySearch] = useState(listing.title);
    // Category options keep the names of the chosen category even when suggestions change.
    const [categoryNames, setCategoryNames] = useState<Record<string, string>>(
        listing.categoryId && listing.categoryName ? {[listing.categoryId]: listing.categoryName} : {}
    );

    useEffect(() => {
        if (open) {
            form.setFieldsValue({
                title: listing.title,
                description: listing.description,
                categoryId: listing.categoryId,
                condition: listing.condition,
                conditionDescription: listing.conditionDescription,
                aspects: listing.aspects,
                price: parseMoney(listing.price),
                buyerShipping: parseMoney(listing.buyerShipping) ?? 0,
            });
            setCategorySearch(listing.title);
        }
    }, [open, listing, form]);

    const suggestionsQuery = ApiQuery.useAdminEbayCategorySuggestionsQuery(
        {q: categorySearch},
        {enabled: open && categorySearch.trim().length >= 3, keepPreviousData: true, staleTime: 10 * 60_000}
    );
    const categoryQuery = ApiQuery.useAdminEbayProductCategoryQuery(
        {id: productId, categoryId: categoryId ?? ''},
        {enabled: open && !!categoryId}
    );
    const category = categoryId ? categoryQuery.data : undefined;

    // A category accepts only some conditions: switch to the suggested one when the current is not among them.
    useEffect(() => {
        if (!category || category.conditions.length === 0) {
            return;
        }
        const current = form.getFieldValue('condition') as string | undefined;
        if (!current || !category.conditions.some((c) => c.value === current)) {
            form.setFieldValue('condition', category.suggestedCondition);
        }
    }, [category, form]);

    const searchCategories = useMemo(() => debounce((q: string) => q.trim() && setCategorySearch(q), 400), []);

    const categoryOptions = useMemo(() => {
        const items = suggestionsQuery.data?.items ?? [];
        const options = items.map((item) => ({
            value: item.id,
            label: (
                <span>
                    {item.name}
                    <Text
                        type="secondary"
                        style={{fontSize: 12}}
                    >
                        {item.path.length > 0 ? ` · ${item.path.join(' › ')}` : ''}
                    </Text>
                </span>
            ),
            name: item.name,
        }));
        if (categoryId && !items.some((item) => item.id === categoryId)) {
            options.unshift({
                value: categoryId,
                label: <span>{categoryNames[categoryId] ?? categoryId}</span>,
                name: '',
            });
        }
        return options;
    }, [suggestionsQuery.data, categoryId, categoryNames]);

    const conditionOptions = (
        category?.conditions.length ? category.conditions.map((c) => c.value) : COMMON_CONDITIONS
    ).map((enumValue) => ({
        value: enumValue,
        label: category?.conditions.find((c) => c.value === enumValue)?.name ?? conditionLabel(enumValue),
    }));

    const requiredAspects = category?.aspects.filter((a) => a.required) ?? [];
    const recommendedAspects = category?.aspects.filter((a) => !a.required && a.recommended) ?? [];

    // Saving a live listing sends the changes to eBay.
    const live = listing.status === 'active';
    const saveMutation = ApiQuery.useAdminEbaySaveDraftMutation({
        onSuccess: (saved) => {
            if (live) {
                notify.openNotificationWithIcon(
                    'info',
                    'Sending the changes to eBay',
                    'The listing stays live meanwhile.'
                );
            } else {
                notify.openNotificationWithIcon(
                    saved.problems.length ? 'info' : 'success',
                    'eBay draft saved',
                    saved.problems.length ? `Before publishing: ${saved.problems.join(' ')}` : 'Ready to publish.'
                );
            }
            onSaved(saved);
        },
        onError: (errors) => notify.openNotificationWithIcon('error', 'Failed to save', errorMessage(errors)),
    });

    const onFinish = (values: FormValues) => {
        const aspects: Record<string, string[]> = {};
        // Keep only the specifics of the chosen category; values typed for another category are dropped.
        const names = new Set(category?.aspects.map((a) => a.name) ?? Object.keys(values.aspects ?? {}));
        Object.entries(values.aspects ?? {}).forEach(([name, list]) => {
            if (names.has(name) && list?.length) {
                aspects[name] = list;
            }
        });
        saveMutation.mutate({
            id: productId,
            params: {
                title: values.title,
                description: values.description ?? '',
                categoryId: values.categoryId,
                categoryName: values.categoryId ? categoryNames[values.categoryId] : undefined,
                condition: values.condition,
                conditionDescription: values.conditionDescription,
                aspects,
                price: values.price,
                buyerShipping: values.buyerShipping ?? 0,
            },
        });
    };

    const hint = listing.priceHint;
    const suggested = parseMoney(hint.suggested);

    return (
        <Drawer
            title={live ? 'eBay listing (live)' : 'eBay listing draft'}
            open={open}
            onClose={onClose}
            width={screenMap.lg ? 760 : '100%'}
            destroyOnClose
            // Esc closes suggestion lists here, and a stray click must not drop the edits.
            keyboard={false}
            maskClosable={false}
            extra={
                <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={saveMutation.isLoading}
                    onClick={() => form.submit()}
                >
                    {live ? 'Save and update on eBay' : 'Save draft'}
                </Button>
            }
        >
            {/* The product page leaves to the list on Esc; here Esc only closes suggestion lists. */}
            <div
                onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                        e.stopPropagation();
                    }
                }}
            >
                <Form<FormValues>
                    form={form}
                    layout="vertical"
                    onFinish={onFinish}
                    requiredMark
                >
                    <Form.Item
                        name="title"
                        label="Title"
                        rules={[{required: true, whitespace: true, message: 'Enter a title'}]}
                        extra="What buyers search for: brand, model, type, key features."
                    >
                        <Input
                            showCount
                            maxLength={MAX_TITLE}
                        />
                    </Form.Item>

                    <Form.Item
                        name="categoryId"
                        label="eBay category"
                        extra="Suggestions for the title; type to search other categories."
                    >
                        <Select
                            showSearch
                            allowClear
                            placeholder="Choose a category"
                            filterOption={false}
                            onSearch={searchCategories}
                            notFoundContent={
                                suggestionsQuery.isFetching ? <Spin size="small" /> : 'Type at least 3 letters'
                            }
                            options={categoryOptions}
                            onChange={(value: string | undefined, option) => {
                                const name = (option as {name?: string} | undefined)?.name;
                                if (value && name) {
                                    setCategoryNames((names) => ({...names, [value]: name}));
                                }
                            }}
                        />
                    </Form.Item>
                    {suggestionsQuery.isError && (
                        <Alert
                            type="error"
                            showIcon
                            style={{marginBottom: 16}}
                            message={errorMessage(suggestionsQuery.error) ?? 'Failed to load eBay categories'}
                        />
                    )}

                    <Flex
                        gap={16}
                        wrap
                    >
                        <Form.Item
                            name="condition"
                            label="Condition"
                            style={{flex: '1 1 240px'}}
                            extra={category ? undefined : 'The category may narrow the list.'}
                        >
                            <Select
                                options={conditionOptions}
                                placeholder="Choose"
                            />
                        </Form.Item>
                        <Form.Item
                            name="conditionDescription"
                            label="Condition details"
                            style={{flex: '2 1 320px'}}
                            extra={condition === 'NEW' ? 'eBay does not show it for new items.' : undefined}
                        >
                            <Input
                                maxLength={1000}
                                placeholder="Tested, small scratches on the side"
                            />
                        </Form.Item>
                    </Flex>

                    {categoryId && (
                        <>
                            <Divider
                                orientation="left"
                                plain
                            >
                                Item specifics
                            </Divider>
                            {categoryQuery.isLoading && <Spin />}
                            {categoryQuery.isError && (
                                <Alert
                                    type="error"
                                    showIcon
                                    message={errorMessage(categoryQuery.error) ?? 'Failed to load the category'}
                                />
                            )}
                            {category && (
                                <Flex
                                    gap={16}
                                    wrap
                                >
                                    {[...requiredAspects, ...recommendedAspects].map((aspect) => (
                                        <div
                                            key={aspect.name}
                                            style={{flex: '1 1 300px'}}
                                        >
                                            <AspectField aspect={aspect} />
                                        </div>
                                    ))}
                                </Flex>
                            )}
                            {category && requiredAspects.length + recommendedAspects.length === 0 && (
                                <Paragraph type="secondary">The category has no required item specifics.</Paragraph>
                            )}
                        </>
                    )}

                    <Divider
                        orientation="left"
                        plain
                    >
                        Price and shipping
                    </Divider>
                    <Flex
                        gap={16}
                        wrap
                    >
                        <Form.Item
                            name="price"
                            label="Price"
                            style={{flex: '1 1 240px'}}
                            extra={
                                suggested !== undefined ? (
                                    <span>
                                        Suggested {formatPrice(suggested)} (market {formatPrice(hint.reference)} with
                                        shipping, floor {formatPrice(hint.floor)}).{' '}
                                        <Button
                                            type="link"
                                            size="small"
                                            style={{padding: 0}}
                                            onClick={() => form.setFieldValue('price', suggested)}
                                        >
                                            Use suggested
                                        </Button>
                                    </span>
                                ) : (
                                    `No eBay market data yet; floor ${formatPrice(hint.floor)}.`
                                )
                            }
                        >
                            <InputNumber
                                prefix="$"
                                min={0.01}
                                step={1}
                                precision={2}
                                style={{width: '100%'}}
                            />
                        </Form.Item>
                        <Form.Item
                            name="buyerShipping"
                            label="Buyer pays for shipping"
                            style={{flex: '1 1 240px'}}
                            extra="0 = free shipping (USPS Ground Advantage)."
                        >
                            <InputNumber
                                prefix="$"
                                min={0}
                                step={1}
                                precision={2}
                                style={{width: '100%'}}
                            />
                        </Form.Item>
                    </Flex>

                    <Form.Item
                        name="description"
                        label="Description"
                        extra="Plain text from the product; it goes into the listing as is, with the condition and our shipping and returns terms."
                    >
                        <Input.TextArea autoSize={{minRows: 5, maxRows: 16}} />
                    </Form.Item>

                    <Space direction="vertical">
                        <Text type="secondary">
                            Photos: {listing.imagesCount} from the product, the primary one first. SKU {listing.sku}.
                        </Text>
                    </Space>
                </Form>
            </div>
        </Drawer>
    );
};
